import { isMainnet } from "@/env";

import { Logger } from "../logger";
import { getDomainId } from "./domains";
import { fetchCCTPMessage } from "./fetch";
import { logCCTPFailureOnce } from "./logger";
import { checkMessageClaimed } from "./nonce";
import { cctpLogContext, type CctpTransferTags } from "./telemetry";
import type { IrisDelayReason } from "./v2/iris";
import { parseCCTPMessageV2 } from "./v2/message";
import type { CCTPVersion } from "./version";

/**
 * Check if Circle has received the CCTP message for the given transaction.
 * Returns true when Circle API returns 200 (message exists), regardless of
 * whether the attestation is ready or still PENDING.
 *
 * Used to detect the `confirming → bridging` transition for CCTP transfers,
 * since CCTP transactions are NOT indexed by the Wormholescan operations API.
 */
export async function checkCircleMessageReceived(
  sourceTxHash: string,
  sourceChain: string,
  transfer?: CctpTransferTags,
): Promise<boolean> {
  const domainId = getDomainId(sourceChain);
  if (domainId === null) {
    return false;
  }

  try {
    const data = await fetchCCTPMessage({ domainId, txHash: sourceTxHash });
    return data !== null;
  } catch (err) {
    logCCTPFailureOnce(
      err,
      { feature: "cctp-message-received-check", sourceChain, sourceTxHash },
      transfer,
    );
    return false;
  }
}

async function fetchMessageBytes(txHash: string, sourceChain: string): Promise<string | null> {
  const domainId = getDomainId(sourceChain);
  if (domainId === null) return null;

  return (await fetchCCTPMessage({ domainId, txHash }))?.message ?? null;
}

export interface BridgeStatusResult {
  isCompleted: boolean;
  destinationTxHash: string | null;
  status: BridgeStatus | null;
}

const WORMHOLESCAN_EXPLORER_URL = isMainnet
  ? "https://wormholescan.io"
  : "https://testnet.wormholescan.io";

export enum BridgeStatus {
  Pending = "pending",
  Completed = "completed",
  Failed = "failed",
  ReadyToClaim = "ready_to_claim",
}

/** Wormholescan link for a v1 transfer; v2 transfers never touch Wormhole, so they get none. */
export function getBridgeTxUrl(
  txHash: string,
  cctpVersion: CCTPVersion | undefined,
): string | null {
  if (cctpVersion === 2) return null;
  return `${WORMHOLESCAN_EXPLORER_URL}/#/tx/${txHash}`;
}

export async function checkCCTPRedemptionStatus(
  txHash: string,
  sourceChain: string,
  cachedMessageBytes?: string,
  transfer?: CctpTransferTags,
): Promise<BridgeStatusResult> {
  const defaultResult: BridgeStatusResult = {
    isCompleted: false,
    destinationTxHash: null,
    status: null,
  };

  if (!txHash) return defaultResult;

  // Use the cached attested message when available, otherwise fetch it from Circle.
  let messageBytes = cachedMessageBytes ?? null;
  if (!messageBytes) {
    try {
      messageBytes = await fetchMessageBytes(txHash, sourceChain);
    } catch (err) {
      logCCTPFailureOnce(
        err,
        { feature: "cctp-redemption-fields-fetch", sourceChain, sourceTxHash: txHash },
        transfer,
      );
    }
  }

  if (!messageBytes) return defaultResult;

  try {
    switch (await checkMessageClaimed(messageBytes)) {
      case "claimed":
        return { isCompleted: true, destinationTxHash: null, status: BridgeStatus.Completed };
      case "unclaimed":
        return { isCompleted: false, destinationTxHash: null, status: BridgeStatus.Pending };
      case "unknown":
        return defaultResult;
    }
  } catch (err) {
    const details = { txHash, sourceChain, err };
    Logger.warn(
      "On-chain nonce check failed",
      transfer ? cctpLogContext(transfer, details) : details,
    );
    return defaultResult;
  }
}

/**
 * Check if Circle attestation is ready for the given transaction. `renewedFrom` is the expired
 * attestation's `expirationBlock`: until Circle's renewal lands it still serves that copy. A
 * renewal can come back as a Standard attestation, whose `expirationBlock` is 0 (never expires).
 */
export async function checkAttestationReady(
  sourceTxHash: string,
  sourceChain: string,
  { renewedFrom, transfer }: { renewedFrom?: bigint; transfer?: CctpTransferTags } = {},
): Promise<
  | { ready: true; attestation: string; messageBytes: string | null }
  | { ready: false; delayReason?: IrisDelayReason }
> {
  const domainId = getDomainId(sourceChain);
  if (domainId === null) {
    return { ready: false };
  }

  try {
    const data = await fetchCCTPMessage({ domainId, txHash: sourceTxHash });
    if (data?.status !== "complete" || !data.attestation) {
      return data?.delayReason ? { ready: false, delayReason: data.delayReason } : { ready: false };
    }
    if (renewedFrom !== undefined) {
      const expirationBlock = data.message
        ? parseCCTPMessageV2(data.message)?.expirationBlock
        : undefined;
      const renewed =
        expirationBlock !== undefined &&
        (expirationBlock === BigInt(0) || expirationBlock > renewedFrom);
      if (!renewed) return { ready: false };
    }
    return { ready: true, attestation: data.attestation, messageBytes: data.message };
  } catch (err) {
    logCCTPFailureOnce(
      err,
      { feature: "cctp-attestation-ready-check", sourceChain, sourceTxHash },
      transfer,
    );
    return { ready: false };
  }
}
